import { z } from 'zod';
import { NotificationChannel, Permission, Role, Severity } from './enums.js';
/* -------------------------------------------------------------------------- */
/* Auth                                                                       */
/* -------------------------------------------------------------------------- */
export const LoginBody = z.object({
    email: z.string().email().max(254),
    password: z.string().min(1).max(256),
});
export const SessionUser = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    role: Role,
    permissions: z.array(Permission),
    visibleZoneKinds: z.array(z.string()).nullable(),
    mustChangePassword: z.boolean(),
    lastLoginAt: z.string().datetime().nullable(),
});
export const ChangePasswordBody = z.object({
    currentPassword: z.string().min(1).max(256),
    newPassword: z.string().min(12).max(256),
});
export const CreateUserBody = z.object({
    email: z.string().email().max(254),
    name: z.string().min(1).max(120),
    role: Role,
    password: z.string().min(12).max(256),
    phoneE164: z
        .string()
        .regex(/^\+[1-9]\d{7,14}$/, 'Phone must be E.164, e.g. +919876543210')
        .nullable()
        .default(null),
});
export const UpdateUserBody = z.object({
    name: z.string().min(1).max(120).optional(),
    role: Role.optional(),
    phoneE164: z.string().regex(/^\+[1-9]\d{7,14}$/).nullable().optional(),
    active: z.boolean().optional(),
});
/* -------------------------------------------------------------------------- */
/* Notification preferences                                                   */
/* -------------------------------------------------------------------------- */
export const NotificationPreference = z.object({
    channel: NotificationChannel,
    enabled: z.boolean(),
    minSeverity: Severity,
    quietHoursStart: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable(),
    quietHoursEnd: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable(),
    /** CRITICAL alerts pierce quiet hours unless this is explicitly false. */
    criticalOverridesQuietHours: z.boolean(),
});
export const UpdatePreferencesBody = z.object({
    preferences: z.array(NotificationPreference).max(NotificationChannel.options.length),
});
/* -------------------------------------------------------------------------- */
/* Dashboard summary                                                          */
/* -------------------------------------------------------------------------- */
export const DashboardSummary = z.object({
    cameras: z.object({
        total: z.number().int(),
        online: z.number().int(),
        degraded: z.number().int(),
        offline: z.number().int(),
        disabled: z.number().int(),
    }),
    occupancy: z.object({
        total: z.number().int(),
        dining: z.number().int(),
        gaming: z.number().int(),
        observedAt: z.string().datetime().nullable(),
    }),
    alerts: z.object({
        active: z.number().int(),
        critical: z.number().int(),
        high: z.number().int(),
        unacknowledged: z.number().int(),
    }),
    today: z.object({
        visitors: z.number().int(),
        peakOccupancy: z.number().int(),
        peakHour: z.number().int().nullable(),
        eventsRecorded: z.number().int(),
    }),
    /**
     * Billing-counter quality of service. Counts are of alerts raised today, not
     * raw detections — i.e. already past the minSustainSeconds / minDwellSeconds
     * gate in alert_rules, so a cashier stepping away for five seconds does not
     * inflate this number.
     */
    qualityOfService: z.object({
        cashierAbsenceCount: z.number().int(),
        customerWaitCount: z.number().int(),
    }),
    system: z.object({
        healthy: z.boolean(),
        services: z.array(z.object({
            service: z.string(),
            healthy: z.boolean(),
            detail: z.string().nullable(),
            observedAt: z.string().datetime().nullable(),
        })),
    }),
    /**
     * True when analytics has too little history to be meaningful. The dashboard
     * shows an explicit "collecting data" state rather than rendering zeros as if
     * they were measurements.
     */
    dataWarmup: z.boolean(),
});
/* -------------------------------------------------------------------------- */
/* Analytics                                                                  */
/* -------------------------------------------------------------------------- */
export const AnalyticsGranularity = z.enum(['hour', 'day', 'week', 'month']);
export const AnalyticsQuery = z.object({
    from: z.string().datetime(),
    to: z.string().datetime(),
    granularity: AnalyticsGranularity.default('hour'),
    zoneId: z.string().uuid().optional(),
    cameraId: z.string().uuid().optional(),
});
export const AnalyticsPoint = z.object({
    bucket: z.string().datetime(),
    entries: z.number().int(),
    exits: z.number().int(),
    avgOccupancy: z.number(),
    peakOccupancy: z.number().int(),
    samples: z.number().int(),
});
export const AnalyticsSeries = z.object({
    granularity: AnalyticsGranularity,
    from: z.string().datetime(),
    to: z.string().datetime(),
    points: z.array(AnalyticsPoint),
    /** Which table actually answered the query — raw samples or a rollup. */
    sourceTable: z.string(),
    /** Buckets with too few samples to trust, so the UI can grey them out. */
    lowConfidenceBuckets: z.array(z.string()),
});
export const HeatmapCell = z.object({
    zoneId: z.string().uuid(),
    zoneName: z.string(),
    kind: z.string(),
    hourOfDay: z.number().int().min(0).max(23),
    dayOfWeek: z.number().int().min(0).max(6),
    avgOccupancy: z.number(),
    peakOccupancy: z.number().int(),
    samples: z.number().int(),
});
/* -------------------------------------------------------------------------- */
/* Generic envelopes                                                          */
/* -------------------------------------------------------------------------- */
export const ApiError = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        /** Field-level validation detail. Never contains internal stack detail. */
        details: z.record(z.unknown()).optional(),
        correlationId: z.string().optional(),
    }),
});
export function paginated(item) {
    return z.object({
        items: z.array(item),
        nextCursor: z.string().nullable(),
        total: z.number().int().nullable(),
    });
}
export const HealthResponse = z.object({
    status: z.enum(['ok', 'degraded', 'down']),
    version: z.string(),
    uptimeSeconds: z.number(),
    checks: z.array(z.object({
        name: z.string(),
        healthy: z.boolean(),
        latencyMs: z.number().nullable(),
        detail: z.string().nullable(),
    })),
});
//# sourceMappingURL=api.js.map